import React, { useState, useEffect } from "react";
import {
    getExaminerCitationsDetails,
    processVerifiedPatents,
    startVijilize,
    addExaminerCitationsToProject,
    createUrl,
    createPatent,
    createPatentPresignedUrl,
} from "services/project";
import Toast from "../Toast";
import allImgPaths from "assets/images";
import SearchBar from "../SearchBar";
import CustomCheckBox from "components/CustomCheckBox";
import { PROJECT_CONSTANTS, VIJILIZE_CONSTANTS, HEATMAP_COLOR_SCALE } from "utils/constant";
import ReactApexChart from "react-apexcharts";
import { InputTextField } from "components/InputField";
import Textarea from "../Textarea";
import useAppState from "context/useAppState";
import CustomSelect from "../CustomSelect";
import MonitorIcon from "@mui/icons-material/Monitor";
import MonitorModal from "./MonitorModal";


interface SimilarProductsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onBack?: () => void;
    patentData: any;
    projectId: string;
    citationsData?: any;
    selectedPatentId?: any;
    callSimilarProductAPI: ({ }: any) => void;
    onSuccess?: () => void;
    topK?: number;
}

interface CitationItem {
    cited_patent: string;
    cited_id: string;
    date: string;
    citationType?: string;
    is_new?: boolean;
}

const SimilarProductsModal: React.FC<SimilarProductsModalProps> = ({
    isOpen,
    onClose,
    onBack,
    patentData,
    projectId,
    citationsData: initialCitationsData,
    selectedPatentId,
    callSimilarProductAPI,
    onSuccess,
    topK = 5
}) => {
    const [isShowMonitor, setIsShowMonitor] = useState(false);

    // Create a wrapper for onClose that resets state
    const handleClose = () => {
        resetState();
        onClose();
    };
    // Two-step modal state
    const [currentStep, setCurrentStep] = useState<"citations" | "vijilize">(
        "citations"
    );

    useEffect(() => {
        if (initialCitationsData) console.log({ initialCitationsData })
    }, [initialCitationsData])

    // Citations step state
    const [selectedCount, setSelectedCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCitations, setSelectedCitations] = useState<string[]>([]);
    const [activeListIndex, setActiveListIndex] = useState(0);
    const [activeListType, setActiveListType] = useState<string>(
        "similar_products"
    );
    const [activeProvider, setActiveProvider] = useState<string>("serper");
    const [isLoading, setIsLoading] = useState(false);
    const [isAddingToProject, setIsAddingToProject] = useState(false);
    const [isVijilizing, setIsVijilizing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [citationsData, setCitationsData] = useState<any>(null);
    const [totalCitations, setTotalCitations] = useState<any>(0);
    const [citations, setCitations] = useState<any>([]);
    const [selectedPatentNumbers, setSelectedPatentNumbers] = useState<string[]>(
        []
    );
    const [documentTransactionHistory, setDocumentTransactionHistory] = useState<
        string | null
    >(null);

    // Vijilize step state
    const [vijilizeName, setVijilizeName] = useState<string>("");
    const [vijilizeDescription, setVijilizeDescription] = useState<string>("");
    const [useOnlyIndependentClaims, setUseOnlyIndependentClaims] = useState<boolean>(false);
    const [combineMainUrl, setCombineMainUrl] = useState<boolean>(true);
    const [activeIndex, setActiveIndex] = useState<number>(1); // Source accordion open by default (1)
    const [sourcePatents, setSourcePatents] = useState<string[]>([]);
    const [comparePatents, setComparePatents] = useState<string[]>([]);
    const [comparePatentsForAPI, setComparePatentsForAPI] = useState<any[]>([]);
    const [citationDataMap, setCitationDataMap] = useState<
        Record<string, CitationItem>
    >({});

    // Selected patent data for source and compare
    const [selectedPatentData, setSelectedPatentData] = useState<any[]>([]);

    // Preliminary ranking state
    const [showPreviewHeatmap, setShowPreviewHeatmap] = useState(false);
    const [insightsView, setInsightsView] = useState<"heatmap" | "list">("heatmap");
    const [rankingPage, setRankingPage] = useState(0);
    const RANKING_PAGE_SIZE = 5;
    const [hoveredRankItem, setHoveredRankItem] = useState<number | null>(null);

    const getRankedResults = () => {
        const allResults: { citation: any; typeKey: string }[] = [];
        const seenUrls = new Set<string>();
        const seenTitles = new Set<string>();
        citationTypes.forEach((type) => {
            (citationsData?.[type.key]?.results || []).forEach((r: any) => {
                if (r.link && r.relevance_score != null) {
                    const title = (r.title || "").toLowerCase().trim();
                    if (!seenUrls.has(r.link) && (!title || !seenTitles.has(title))) {
                        seenUrls.add(r.link);
                        if (title) seenTitles.add(title);
                        allResults.push({ citation: r, typeKey: type.key });
                    }
                }
            });
        });
        allResults.sort((a, b) => (b.citation.relevance_score ?? 0) - (a.citation.relevance_score ?? 0));
        return allResults;
    };

    const handleRankingItemClick = (item: { citation: any; typeKey: string }) => {
        const citationId = `citation-${item.typeKey}-${item.citation.title}`;
        const isCurrentlySelected = selectedCitations.includes(citationId);
        if (isCurrentlySelected) {
            setSelectedCitations(selectedCitations.filter((id) => id !== citationId));
            setSelectedCount(selectedCount - 1);
        } else {
            setSelectedCitations([...selectedCitations, citationId]);
            setSelectedCount(selectedCount + 1);
        }
    };

    // Heatmap chart options (for heatmap view)
    const heatmapCitationMapRef = React.useRef<{ title: string; typeKey: string }[]>([]);

    const buildHeatmapSeries = () => {
        const ranked = getRankedResults().slice(0, 10);
        heatmapCitationMapRef.current = ranked.map((r) => ({
            title: r.citation.title,
            typeKey: r.typeKey,
        }));
        return [{
            name: patentData?.patent_number || "Source Patent",
            data: ranked.map((r) => ({
                x: (r.citation.title || r.citation.link || "").substring(0, 20) +
                    ((r.citation.title?.length || 0) > 20 ? "..." : ""),
                y: Math.round((r.citation.relevance_score ?? 0) * 100),
            })),
        }];
    };

    const heatmapOptions: any = {
        chart: {
            type: "heatmap",
            toolbar: { show: false },
            events: {
                click: (_event: any, _chartContext: any, opts: any) => {
                    const { dataPointIndex } = opts;
                    if (dataPointIndex >= 0) {
                        const mapping = heatmapCitationMapRef.current[dataPointIndex];
                        if (mapping) handleRankingItemClick({ citation: { title: mapping.title }, typeKey: mapping.typeKey });
                    }
                },
            },
            selection: { enabled: false },
        },
        dataLabels: {
            enabled: true,
            formatter: (val: number) => `${val}%`,
            style: { fontSize: "11px", fontWeight: 600, colors: ["#1e3a5f"] },
        },
        plotOptions: {
            heatmap: {
                shadeIntensity: 0.5,
                colorScale: { ranges: HEATMAP_COLOR_SCALE },
            },
        },
        xaxis: {
            labels: {
                rotate: -45,
                rotateAlways: true,
                style: { fontSize: "9px", colors: "#3062D4", fontWeight: 500 },
                trim: true,
                maxHeight: 80,
            },
        },
        yaxis: {
            labels: { style: { fontSize: "11px", fontWeight: 600, colors: ["#3062D4"] } },
        },
        tooltip: { y: { formatter: (val: number) => `${val}% relevance` } },
    };

    // Define citation types
    const citationTypes = [
        {
            key: "similar_products",
            title: "Similar Products",
        },
        {
            key: "similar_patents",
            title: "Similar Patents",
        },
        {
            key: "prior_art_results",
            title: "Prior Art"
        }
    ];

    // Function to reset all state variables to their default values
    const resetState = () => {
        // Reset two-step modal state
        setCurrentStep("citations");

        // Reset citations step state
        setSelectedCount(0);
        setSearchQuery("");
        setSelectedCitations([]);
        setActiveListIndex(0);
        setActiveListType("similar_products");
        setActiveProvider("serper");
        setIsLoading(false);
        setIsAddingToProject(false);
        setIsVijilizing(false);
        setError(null);
        setCitationsData(null);
        setTotalCitations(0);
        setCitations([]);
        setSelectedPatentNumbers([]);
        setDocumentTransactionHistory(null);

        // Reset vijilize step state
        setVijilizeName("");
        setVijilizeDescription("");
        setActiveIndex(1); // Source accordion open by default
        setSourcePatents([]);
        setComparePatents([]);
        setCitationDataMap({});

        // Reset selected patent data
        setSelectedPatentData([]);
    };

    useEffect(() => {
        if (isOpen && patentData?.id && patentData?.patent_number) {
            setCitations([]);
            setCitationsData([]);
            setSearchQuery("");
            fetchExaminerCitations();
        }
    }, [isOpen, patentData?.id, patentData?.patent_number, initialCitationsData]);

    // Effect to reset state when modal is closed
    useEffect(() => {
        if (!isOpen) {
            resetState();
        }
    }, [isOpen]);

    useEffect(() => {
        setCitations([]);
        if (isOpen && citationsData) {
            const newCitations = citationsData[activeListType] || [];
            setCitations(newCitations);
        }
        return () => {
            setCitations([]);
        };
    }, [isOpen, activeListType, citationsData]);

    // Update activeListType when activeListIndex changes
    useEffect(() => {
        setActiveListType(citationTypes[activeListIndex].key);
    }, [activeListIndex]);

    const isValidPatentNumber = (citation: string): boolean => {
        return /^US\d+/.test(citation);
    };

    const fetchExaminerCitations = async () => {
        setIsLoading(true);
        setError(null);
        setSelectedCitations([]);
        setSelectedCount(0);

        try {
            // Use initialCitationsData if provided, otherwise fetch from API
            if (initialCitationsData) {
                setCitationsData(initialCitationsData);
                processExaminerCitationsData(initialCitationsData);
            } else {
                // let selectedTeam: any = JSON.parse(
                //     localStorage.getItem("selectedTeam") as string
                // );
                // let team_id: string = selectedTeam.currentTeamId;
                // const response = await getExaminerCitationsDetails(
                //     patentData.patent_number,
                //     patentData.s3_file_path,
                //     projectId,
                //     team_id
                // );
                // setCitationsData(response.data);
                // processExaminerCitationsData(response.data);
            }
        } catch (error) {
            console.error("Error processing examiner citations:", error);
            setError("Failed to load examiner citations. Please try again later.");
            Toast.error("Error", "Failed to load examiner citations");
        } finally {
            setIsLoading(false);
        }
    };

    const processExaminerCitationsData = (data: any) => {
        console.log({ data })
        // Calculate total citations with proper typing
        const total = Object.values(data || {})?.reduce<number>((acc: any, citations: any) => {
            if (Array.isArray(citations?.results)) {
                return acc + citations?.results.length;
            }
            return acc;
        }, 0);
        console.log({ total })
        setTotalCitations(total);

        // Find first non-empty list
        const firstNonEmptyIndex = citationTypes.findIndex(
            (type) => (data?.[type.key]?.results?.length || 0) > 0
        );
        if (firstNonEmptyIndex !== -1) {
            setActiveListIndex(firstNonEmptyIndex);
        }

        // Check if document_transaction_history exists in the response
        if (data?.document_transaction_history) {
            setDocumentTransactionHistory(data.document_transaction_history);
        } else {
            setDocumentTransactionHistory(null);
        }

        // Auto-select top K results by relevance score across all categories
        // Collect all, sort by score first, then dedup (highest score version wins)
        const rawResults: { citation: any; typeKey: string }[] = [];
        citationTypes.forEach((type) => {
            (data?.[type.key]?.results || []).forEach((citation: any) => {
                if (citation?.link) {
                    rawResults.push({ citation, typeKey: type.key });
                }
            });
        });

        // Sort by score descending so dedup keeps the highest-scored version
        rawResults.sort((a, b) => (b.citation.relevance_score ?? 0) - (a.citation.relevance_score ?? 0));

        // Deduplicate by URL and title
        const allResults: { citation: any; typeKey: string }[] = [];
        const seenUrls = new Set<string>();
        const seenTitles = new Set<string>();
        rawResults.forEach((r) => {
            const url = r.citation.link;
            const title = (r.citation.title || "").toLowerCase().trim();
            if (!seenUrls.has(url) && (!title || !seenTitles.has(title))) {
                seenUrls.add(url);
                if (title) seenTitles.add(title);
                allResults.push(r);
            }
        });

        const topResults = allResults.slice(0, topK);

        if (topResults.length > 0) {
            const preSelected = topResults.map(
                (r) => `citation-${r.typeKey}-${r.citation.title}`
            );
            setSelectedCitations(preSelected);
            setSelectedCount(preSelected.length);
        }
    };

    const handleSelectAll = () => {
        // Get all valid citations that match the current search filter
        const validCitations: string[] = [];

        // Check if there's a search query
        if (searchQuery.trim() !== "") {
            // Apply the search filter across ALL citation types, not just the active one
            citationTypes.forEach((type) => {
                const listData = citationsData?.[type.key] || [];

                // Filter citations based on search query
                const filteredCitations = listData.filter((citation: CitationItem) => {
                    // Normalize both the citation patent number and search query
                    const normalizedCitation = citation.cited_patent
                        .toLowerCase()
                        .replace(/[,\/]/g, "");
                    const normalizedQuery = searchQuery
                        .toLowerCase()
                        .replace(/[,\/]/g, "");

                    return (
                        normalizedCitation.includes(normalizedQuery) ||
                        citation.cited_patent
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase())
                    );
                });

                // Only include valid patent numbers from the filtered results
                filteredCitations.forEach((citation: CitationItem) => {
                    if (isValidPatentNumber(citation.cited_patent)) {
                        validCitations.push(`citation-${type.key}-${citation.cited_id}`);
                    }
                });
            });
        } else {
            // If no search query, use the original logic for all citation types
            citationTypes.forEach((type) => {
                const listData = citationsData?.[type.key]?.results || [];
                listData.forEach((citation: CitationItem) => {
                    // Only include valid patent numbers
                    if (isValidPatentNumber(citation.cited_patent)) {
                        validCitations.push(`citation-${type.key}-${citation.cited_id}`);
                    }
                });
            });
        }

        // Calculate total valid citations count
        const totalValidCitationsCount = validCitations.length;

        // If all valid citations are already selected, deselect all
        if (selectedCount === totalValidCitationsCount) {
            setSelectedCitations([]);
            setSelectedCount(0);
        } else {
            // Otherwise, select all valid citations
            setSelectedCitations(validCitations);
            setSelectedCount(totalValidCitationsCount);
        }
    };

    const getSelectedCountForCurrentList = () => {
        return citations.filter((citation: CitationItem) =>
            selectedCitations.includes(
                `citation-${activeListType}-${citation.cited_id}`
            )
        ).length;
    };

    // Function to get selected count for a specific list type
    const getSelectedCountForListType = (listType: string) => {
        const listData = citationsData?.[listType]?.results || [];
        return listData.filter((citation: any) =>
            selectedCitations.includes(`citation-${listType}-${citation?.title}`)
        ).length;
    };

    const handleCitationSelect = (citationId: string, isChecked: boolean) => {
        // Extract the citation from the citations array
        const citation = citations?.results?.find(
            (c: any) => `citation-${activeListType}-${c.title}` === citationId
        );

        // Only proceed if it's a valid patent number
        // if (citation && isValidPatentNumber(citation.cited_patent)) {
        if (citation) {
            if (isChecked) {
                setSelectedCitations([...selectedCitations, citationId]);
                setSelectedCount(selectedCount + 1);
            } else {
                setSelectedCitations(
                    selectedCitations.filter((id) => id !== citationId)
                );
                setSelectedCount(selectedCount - 1);
            }

            // Update the selected patent numbers list
            // const patentNumber = citation.cited_patent;
            // if (isChecked) {
            //     setSelectedPatentNumbers([...selectedPatentNumbers, patentNumber]);
            // } else {
            //     setSelectedPatentNumbers(
            //         selectedPatentNumbers.filter((num) => num !== patentNumber)
            //     );
            // }
        }
    };

    const handleClear = () => {
        setSelectedCitations([]);
        setSelectedCount(0);
    };

    const handleAddToProject = async () => {
        // Prevent multiple API calls if already in progress
        if (isAddingToProject) return;

        try {
            // Set loading state
            setIsAddingToProject(true);

            // Collect all selected citations from all list types using the same logic as handleVijilize
            let allSelectedCitations: CitationItem[] = [];
            const seenNormalizedPatents = new Set<string>();
            const uniqueSelectedCitations: any[] = [];
            const patentSourcesMap: Record<string, string> = {};

            // Go through each citation type and collect selected items, filtering duplicates by normalized patent number
            citationTypes.forEach((type) => {
                const citationsOfType = citationsData?.[type.key]?.results || [];
                const selectedCitationsOfType = citationsOfType.filter(
                    (citation: any) =>
                        selectedCitations.includes(
                            `citation-${type.key}-${citation.title}`
                        )
                );
                selectedCitationsOfType.forEach((citation: any) => {
                    const normalizedPatent = citation.title.replace(/[,\/]/g, "");
                    if (!seenNormalizedPatents.has(normalizedPatent)) {
                        seenNormalizedPatents.add(normalizedPatent);
                        uniqueSelectedCitations.push({
                            ...citation,
                            citationType: type.title,
                            keyName: type.key
                        });
                    }
                });
            });

            // Extract patent numbers and filter for valid ones only
            const allSelectedPatents = uniqueSelectedCitations
                // .filter((citation: any) =>
                //     isValidPatentNumber(citation?.title?.replace(/[,\/]/g, ""))
                // )
                .map((citation: any) => citation?.title?.replace(/[,\/]/g, ""));

            // If no valid patents were selected, show an error and return
            if (allSelectedPatents.length === 0) {
                Toast.error("Error", "No valid patent numbers were selected");
                return;
            }

            // Prepare patent sources map using the same format as in handleSubmitVijilize
            uniqueSelectedCitations.forEach((citation: any) => {
                const patentNumber = citation?.title?.replace(/[,\/]/g, "");

                // Skip if not a valid patent number
                // if (!isValidPatentNumber(patentNumber)) return;

                let dateString;
                if (citation.date) {
                    try {
                        const dateObj = new Date(citation.date);
                        if (!isNaN(dateObj.getTime())) {
                            dateString = dateObj.toLocaleDateString("en-US", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                            });
                        } else {
                            dateString = citation.date;
                        }
                    } catch (e) {
                        dateString = citation.date;
                    }
                } else {
                    dateString = new Date().toLocaleDateString("en-US", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                    });
                }

                const sourceField = `${patentData.patent_number} - ${citation.citationType} - ${dateString}`;
                patentSourcesMap[patentNumber] = sourceField;
            });

            const selectedTeam = localStorage.getItem("selectedTeam")
                ? JSON.parse(localStorage.getItem("selectedTeam") as string)
                : null;
            const teamId = selectedTeam?.currentTeamId;

            // Create the payload for the API
            // const addToProjectPayload = {
            //     patent_numbers: allSelectedPatents,
            //     team_id: teamId,
            //     project_id: projectId,
            //     patent_category: PROJECT_CONSTANTS.COMPARE,
            //     patent_type: PROJECT_CONSTANTS.URL,
            //     examiner_patent_sources: patentSourcesMap,
            // };

            // console.log({ addToProjectPayload })

            // Call the API to add citations to the project
            // await addExaminerCitationsToProject(addToProjectPayload);

            if (uniqueSelectedCitations?.length) {
                // Create a mutable clone once outside the loop
                let updatedCitationsData = { ...citationsData };

                let urlDataResponse: any;
                for (const element of uniqueSelectedCitations) {
                    if (element?.keyName == 'similar_products' || element?.keyName == 'prior_art_results') {

                        let payload: any = {
                            url: element?.link,
                            url_label: extractTitleFromUrl(element?.link, element?.title),
                            project_id: projectId,
                            team_id: selectedTeam.currentTeamId,
                        };

                        const { data: productDataResponse } = await createUrl(payload);
                        urlDataResponse = productDataResponse
                    }
                    if (element?.keyName == 'similar_patents') {

                        const payload = {
                            patent_numbers: [
                                element.title.split('-')[0].trim()
                            ],
                            project_id: projectId,
                            team_id: selectedTeam.currentTeamId,
                            patent_category: PROJECT_CONSTANTS.COMPARE,
                            patent_type: PROJECT_CONSTANTS.PATENT,
                            patent_source: "With Patent Number"
                        }

                        await processVerifiedPatents(payload)
                    }

                    const keyName = element?.keyName;
                    const keyData = updatedCitationsData?.[keyName];

                    if (keyData?.results?.length) {
                        const allKeyData = [...keyData.results]; // clone results array
                        const index = allKeyData.findIndex((akd: any) => akd?.link === urlDataResponse?.url);

                        if (index > -1) {
                            allKeyData[index] = {
                                ...allKeyData[index],
                                _id: urlDataResponse?._id,
                            };

                            // Mutate the copy
                            updatedCitationsData[keyName] = {
                                ...keyData,
                                results: allKeyData,
                            };
                        }
                    }
                }

                // Final update AFTER the loop
                setCitationsData(updatedCitationsData);
            }

            // Show success message
            Toast.success(
                "Success",
                `Adding ${allSelectedPatents.length} URL${allSelectedPatents.length !== 1 ? "s" : ""
                } to project started. Check the URL's tab for progress.`
            );

            // Refresh project data to show updated numbers
            if (onSuccess) {
                onSuccess();
            }
        } catch (error: any) {
            console.error("Error adding patents to project:", error);

            Toast.error(
                "Error",
                "Failed to add citations to project. Please try again."
            );
        } finally {
            // Reset loading state
            setIsAddingToProject(false);
        }
    };

    const extractTitleFromUrl = (url: any, title: any) => {
        try {
            const parsedUrl = new URL(url);
            const pathParts = parsedUrl.pathname.split('/').filter(Boolean); // remove empty segments
            // Handle known patterns
            if (parsedUrl.hostname.includes('patents.google.com') && pathParts[0] === 'patent') {
                return pathParts[1]; // US5267366A
            }

            // For general product URLs like Amazon
            const dpIndex = pathParts.indexOf('dp');
            if (dpIndex > 0) {
                return pathParts[dpIndex - 1]; // Title before "dp"
            }

            // Fallback: return last non-empty segment
            return pathParts[pathParts.length - 1] || title;
        } catch (e) {
            return null;
        }
    }

    const handleVijilize = () => {
        // Prevent multiple clicks
        if (isLoading) return;

        // Set loading temporarily to prevent multiple clicks
        setIsLoading(true);

        // Collect all selected citations from all list types
        let allSelectedCitations: CitationItem[] = [];
        const seenNormalizedPatents = new Set<string>();
        const uniqueSelectedCitations: CitationItem[] = [];

        // Go through each citation type and collect selected items, filtering duplicates by normalized patent number
        citationTypes.forEach((type) => {
            const citationsOfType = citationsData?.[type.key]?.results || [];
            const selectedCitationsOfType = citationsOfType.filter(
                (citation: any) =>
                    selectedCitations.includes(
                        `citation-${type.key}-${citation.title}`
                    )
            );
            selectedCitationsOfType.forEach((citation: any) => {
                const normalizedPatent = citation.title.replace(/[,\/]/g, "");
                if (!seenNormalizedPatents.has(normalizedPatent)) {
                    seenNormalizedPatents.add(normalizedPatent);
                    uniqueSelectedCitations.push({
                        ...citation,
                        citationType: type.title
                    });
                }
            });
        });

        console.log({ uniqueSelectedCitations })

        if (uniqueSelectedCitations.length === 0) {
            Toast.warning("Warning", "No references selected");
            return;
        }

        // Extract and normalize patent numbers by removing commas and slashes
        const patentNumbers = uniqueSelectedCitations.map((citation: any) => {
            return citation?.title?.replace(/[,\/]/g, "");
        });

        // Store the selected patent numbers for the next step
        setSelectedPatentNumbers(patentNumbers);

        const citationDataMap = uniqueSelectedCitations.reduce((map, citation: any) => {
            const normalizedPatent = citation?.title?.replace(/[,\/]/g, "");
            map[normalizedPatent] = citation;
            return map;
        }, {} as Record<string, CitationItem>);

        setCitationDataMap(citationDataMap);

        setVijilizeName("");
        setVijilizeDescription("");
        const selectedPatentDataArray = [
            // Add the current patent as a source patent
            {
                patent_id: patentData.id,
                file_name: patentData.patent_number,
            },
            ...uniqueSelectedCitations.map((citation) => ({
                patent_id: citation.cited_id,
                file_name: citation.cited_patent,
            })),
        ];

        setSelectedPatentData(selectedPatentDataArray);

        // Set the source patent to be the current patent
        setSourcePatents([patentData.id]);

        // Initialize all citation patents as compare patents (no duplicates)
        setComparePatents(
            uniqueSelectedCitations.map((citation: any) => citation.title)
        );
        setComparePatentsForAPI(uniqueSelectedCitations)

        // Move to the second step
        setCurrentStep("vijilize");

        // Reset loading state
        setIsLoading(false);
    };

    // In the left sidebar click handler
    const handleListClick = (index: number, type: string) => {
        setActiveListIndex(index);
        setActiveListType(type);
    };

    // Handle name change in the vijilize form
    const handleNameChange = (e: any) => {
        setVijilizeName(e.target.value);
    };

    // Handle description change in the vijilize form
    const handleDescriptionChange = (e: any) => {
        setVijilizeDescription(e.target.value);

        // Adjust textarea height based on content
        let getTextArea = document.getElementById("vijilize-description");
        if (getTextArea) {
            if (e.target.value.length === 0) {
                e.target.style.height = "24px";
            } else {
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
            }
        }
    };

    // Toggle accordion sections in the vijilize form
    const toggleAccordion = (index: number) => {
        setActiveIndex(activeIndex === index ? 0 : index);
    };

    // Check if the submit button should be disabled
    const isSubmitDisabled = () => {
        return !vijilizeName.trim();
    };

    // Check if a citation type contains any new citations
    const hasCitationTypeNewItems = (typeKey: string) => {
        const citations = citationsData?.[typeKey]?.results || [];
        return citations?.some((citation: CitationItem) => citation.is_new);
    };

    // Auto vijilize: skip name/description step, generate name and submit directly
    const handleAutoVijilize = async () => {
        if (isLoading || isVijilizing) return;

        // Collect selected citations (same logic as handleVijilize)
        const seenNormalizedPatents = new Set<string>();
        const uniqueSelectedCitations: CitationItem[] = [];

        citationTypes.forEach((type) => {
            const citationsOfType = citationsData?.[type.key]?.results || [];
            const selectedCitationsOfType = citationsOfType.filter(
                (citation: any) =>
                    selectedCitations.includes(
                        `citation-${type.key}-${citation.title}`
                    )
            );
            selectedCitationsOfType.forEach((citation: any) => {
                const normalizedPatent = citation.title.replace(/[,\/]/g, "");
                if (!seenNormalizedPatents.has(normalizedPatent)) {
                    seenNormalizedPatents.add(normalizedPatent);
                    uniqueSelectedCitations.push({
                        ...citation,
                        citationType:
                            citationTypes.find((t) => t.key === activeListType)
                                ?.title || "Unknown",
                    });
                }
            });
        });

        if (uniqueSelectedCitations.length === 0) {
            Toast.warning("Warning", "No references selected");
            return;
        }

        setIsVijilizing(true);

        try {
            const selectedTeam = localStorage.getItem("selectedTeam")
                ? JSON.parse(localStorage.getItem("selectedTeam") as string)
                : null;
            const teamId = selectedTeam?.currentTeamId;

            const sourcePatentData = {
                patent_id: patentData.id,
                file_id: patentData.file_id,
                file_name: patentData.patent_number,
                s3_file_path: patentData.s3_file_path,
                key: patentData.key,
            };

            // Auto-generate name
            const autoName = `${patentData.patent_number} - Auto Vijilize (${uniqueSelectedCitations.length} targets)`;

            // Create URL documents for items without _id
            const urlWithIds = (uniqueSelectedCitations as any[]).filter((cp: any) => cp?._id);
            const urlWithNoIds = (uniqueSelectedCitations as any[]).filter((cp: any) => !cp?._id);

            if (urlWithNoIds?.length) {
                for (let i = 0; i < urlWithNoIds.length; i++) {
                    const element: any = urlWithNoIds[i];
                    const payload: any = {
                        url: element?.link,
                        url_label: extractTitleFromUrl(element?.link, element?.title),
                        project_id: projectId,
                        team_id: teamId,
                    };
                    const { data: urlDataResponse } = await createUrl(payload);
                    if (urlDataResponse?._id) {
                        urlWithNoIds[i]._id = urlDataResponse?._id;
                    }
                }
            }

            const totalUrls = [...urlWithIds, ...urlWithNoIds];

            const vijilizePayload = {
                type: VIJILIZE_CONSTANTS.COMPARER,
                data: {
                    patent_numbers: uniqueSelectedCitations.map((c: any) =>
                        c?.title?.replace(/[,\/]/g, "")
                    ),
                    team_id: teamId,
                    project_id: projectId,
                    name: autoName,
                    description: `Auto vijilization for ${patentData.patent_number} against ${uniqueSelectedCitations.length} selected search result(s)`,
                    source: [sourcePatentData],
                    patent_category: PROJECT_CONSTANTS.COMPARE,
                    patent_type: PROJECT_CONSTANTS.PATENT,
                    compare: totalUrls
                        .filter((cp: any) => cp?._id)
                        .map((cp: any) => ({
                            _id: cp._id,
                            url: cp?.link,
                            url_label: extractTitleFromUrl(cp?.link, cp?.title),
                        })),
                    useOnlyIndependentClaims: false,
                    combineMainUrl: true,
                },
            };

            if (!vijilizePayload?.data?.compare?.length) {
                Toast.error("Failed", "No URLs to compare");
                return;
            }

            await startVijilize(vijilizePayload);
            Toast.success(
                "Success",
                `Auto vijilization started for ${uniqueSelectedCitations.length} target(s).`
            );
            handleClose();
        } catch (error: any) {
            console.error("Error in auto vijilize:", error);
            Toast.error("Error", "Failed to start auto vijilisation.");
        } finally {
            setIsVijilizing(false);
        }
    };

    // Go back to the citations selection step
    const handleBack = () => {
        setCurrentStep("citations");
    };

    // Handle the final vijilize submission
    const handleSubmitVijilize = async () => {
        if (isSubmitDisabled() || isVijilizing) {
            return;
        }

        // Set loading state
        setIsVijilizing(true);

        try {
            // Show loading toast
            // Toast.info("Processing", "Adding patents to your project...");

            // Get team ID from localStorage
            const selectedTeam = localStorage.getItem("selectedTeam")
                ? JSON.parse(localStorage.getItem("selectedTeam") as string)
                : null;
            const teamId = selectedTeam?.currentTeamId;

            // Get the active citation type title
            const activeCitationType =
                citationTypes.find((type) => type.key === activeListType)?.title ||
                "Unknown";

            // Create source patent data directly from patentData with file_id
            const sourcePatentData = {
                patent_id: patentData.id,
                file_id: patentData.file_id, // Use file_id from patentData
                file_name: patentData.patent_number,
                s3_file_path: patentData.s3_file_path,
                key: patentData.key,
            };

            const patentSourcesMap: Record<string, string> = {};

            selectedPatentNumbers.forEach((patentNumber) => {
                const citation = citationDataMap[patentNumber];
                if (citation) {
                    let dateString;

                    if (citation.date) {
                        try {
                            const dateObj = new Date(citation.date);

                            if (!isNaN(dateObj.getTime())) {
                                dateString = dateObj.toLocaleDateString("en-US", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                });
                            } else {
                                dateString = citation.date;
                            }
                        } catch (e) {
                            dateString = citation.date;
                        }
                    } else {
                        dateString = new Date().toLocaleDateString("en-US", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                        });
                    }

                    const citationType = citation.citationType || activeCitationType;

                    const sourceField = `${patentData.patent_number} - ${citationType} - ${dateString}`;
                    patentSourcesMap[patentNumber] = sourceField;
                }
            });

            const urlWithIds = comparePatentsForAPI.filter((cp: any) => cp?._id)
            const urlWithNoIds = comparePatentsForAPI.filter((cp: any) => !cp?._id)

            if (urlWithNoIds?.length) {
                for (let i = 0; i < urlWithNoIds.length; i++) {
                    const element = urlWithNoIds[i];

                    const payload: any = {
                        url: element?.link,
                        url_label: extractTitleFromUrl(element?.link, element?.title),
                        project_id: projectId,
                        team_id: selectedTeam?.currentTeamId,
                    };

                    const { data: urlDataResponse } = await createUrl(payload);

                    if (urlDataResponse?._id) {
                        urlWithNoIds[i]._id = urlDataResponse?._id;
                    }
                }
            }

            const totalUrls = [...urlWithIds, ...urlWithNoIds]

            const vijilizePayload = {
                type: VIJILIZE_CONSTANTS.COMPARER,
                data: {
                    patent_numbers: selectedPatentNumbers,
                    team_id: teamId,
                    project_id: projectId,
                    name: vijilizeName,
                    description: vijilizeDescription,
                    source: [sourcePatentData],
                    patent_category: PROJECT_CONSTANTS.COMPARE,
                    patent_type: PROJECT_CONSTANTS.PATENT,
                    examiner_patent_sources: patentSourcesMap,
                    hide_patents: true,
                    compare: totalUrls.filter((cp: any) => cp?._id).map((cp: any) => ({ _id: cp._id, url: cp?.link, url_label: extractTitleFromUrl(cp?.link, cp?.title) })),
                    useOnlyIndependentClaims: useOnlyIndependentClaims,
                    combineMainUrl: combineMainUrl,
                }
            };

            // console.log({ vijilizePayload });
            if (!vijilizePayload?.data?.compare?.length) {
                Toast.error('Failed', 'No URLs to compare')
                return
            }

            // Start the vijilization with the examiner_citations type
            const response = await startVijilize(vijilizePayload);

            // Show success toast
            Toast.success(
                "Success",
                `Similar Arts vijilization started successfully.`
            );

            // Close the modal
            handleClose();
        } catch (error: any) {
            console.error("Error processing examiner citations vijilize:", error);

            Toast.error("Error", "Failed to process vijilization. Please try again.");
        } finally {
            // Reset loading state
            setIsVijilizing(false);
        }
    };

    if (!isOpen) return null;

    const handleAPIChange = (value: any) => {
        const requestedK = Number(value);

        // Collect all results, sort by score, then dedup (highest score wins)
        const rawResults: { citation: any; typeKey: string }[] = [];
        citationTypes.forEach((type) => {
            (citationsData?.[type.key]?.results || []).forEach((r: any) => {
                if (r?.link) {
                    rawResults.push({ citation: r, typeKey: type.key });
                }
            });
        });
        rawResults.sort((a, b) => (b.citation.relevance_score ?? 0) - (a.citation.relevance_score ?? 0));

        const allUnique: { citation: any; typeKey: string }[] = [];
        const seenUrls = new Set<string>();
        const seenTitles = new Set<string>();
        rawResults.forEach((r) => {
            const title = (r.citation.title || "").toLowerCase().trim();
            if (!seenUrls.has(r.citation.link) && (!title || !seenTitles.has(title))) {
                seenUrls.add(r.citation.link);
                if (title) seenTitles.add(title);
                allUnique.push(r);
            }
        });

        if (allUnique.length >= requestedK) {
            // We have enough — just re-select top K by relevance score
            allUnique.sort((a, b) => (b.citation.relevance_score ?? 0) - (a.citation.relevance_score ?? 0));
            const topResults = allUnique.slice(0, requestedK);
            const preSelected = topResults.map(
                (r) => `citation-${r.typeKey}-${r.citation.title}`
            );
            setSelectedCitations(preSelected);
            setSelectedCount(preSelected.length);
        } else {
            // Not enough results — fetch more from API
            callSimilarProductAPI({ num_results: requestedK, patentId: selectedPatentId });
        }
    }

    return (
        <div className="fixed inset-0 z-[999] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
                <div className="fixed inset-0 bg-black opacity-30"></div>

                <div className="relative bg-white rounded-lg w-full max-w-[1000px] max-h-[90vh] overflow-hidden">
                    {/* Header */}
                    <div className="px-6 pt-4 pb-2 flex items-center gap-x-2">
                        <div className="flex-1">
                            <h2 className="text-base font-interSemiBold text-[#0A090B]">
                                {currentStep === "vijilize" ? "Vijilize" : `Similar Arts Search - ${patentData.patent_number}`}
                            </h2>
                            <p className="text-xs font-interRegular text-[#4F4D55]">
                                {currentStep === "vijilize" ? "Enter details for this vijilization" : "Find and compare similar products, patents, and prior art"}
                            </p>
                        </div>
                        <button
                            className="flex items-center gap-x-1.5 border border-[#D0D5DD] text-[#7F7D83] rounded-lg px-3 py-1.5 text-sm font-interMedium hover:bg-[#F9FAFB] flex-shrink-0"
                            onClick={() => setIsShowMonitor(true)}
                        >
                            <MonitorIcon sx={{ width: 18, height: 18, color: "#ADACB0" }} />
                            Monitor
                        </button>

                        <div className="flex items-center gap-x-1 flex-shrink-0">
                            {onBack && (
                                <button
                                    onClick={onBack}
                                    className="hover:bg-[#f2f4f7] rounded p-1.5"
                                    title="Back to Search Queries"
                                >
                                    <img
                                        src={allImgPaths.backArrowBlue}
                                        alt="Back"
                                        className="w-4 h-4"
                                    />
                                </button>
                            )}
                            <button
                                onClick={handleClose}
                                className="hover:bg-[#f2f4f7] rounded p-1.5"
                            >
                                <img
                                    src={allImgPaths.closeIcon}
                                    alt="Close"
                                    className="w-3 h-3"
                                />
                            </button>
                        </div>
                    </div>

                    {currentStep === "citations" ? (
                        <>
                            {/* Citations Step Content */}
                            {/* Selection Header */}
                            <div className="px-6 py-2">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-interSemiBold text-[#000000]">
                                        Select Similar Art to vijilize or add to your project
                                    </h3>
                                    <div className="flex items-center gap-x-4">
                                        <span className="text-xs font-interRegular text-[#4F4D55]">
                                            {selectedCount} of {totalCitations} Selected
                                        </span>
                                        <button
                                            onClick={() => setShowPreviewHeatmap(!showPreviewHeatmap)}
                                            title="Preview relevance heatmap"
                                            className={`flex items-center gap-1 px-2.5 py-1 text-xs font-interMedium rounded-md border transition-colors ${
                                                showPreviewHeatmap
                                                    ? "bg-blue-100 text-blue-800 border-blue-300"
                                                    : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                                            }`}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                                            </svg>
                                            Insights
                                        </button>
                                        <button
                                            onClick={handleSelectAll}
                                            className="text-xs text-[#3062D4] hover:text-blue-800 font-interRegular"
                                        >
                                            Select All
                                        </button>
                                        <button
                                            onClick={handleClear}
                                            className="text-xs text-[#3062D4] hover:text-blue-800 font-interRegular"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Preview Ranking Panel */}
                            {showPreviewHeatmap && (() => {
                                const ranked = getRankedResults();
                                const totalPages = Math.ceil(ranked.length / RANKING_PAGE_SIZE);
                                const pageResults = ranked.slice(rankingPage * RANKING_PAGE_SIZE, (rankingPage + 1) * RANKING_PAGE_SIZE);
                                const bestMatch = ranked[0];
                                const bestScore = bestMatch ? Math.round((bestMatch.citation.relevance_score ?? 0) * 100) : 0;

                                return (
                                    <div className="px-6 pb-4">
                                        <div className="border border-[#d0daf5] bg-white rounded-lg overflow-hidden">
                                            {/* Header with toggle */}
                                            <div className="px-5 pt-3 pb-2 flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div>
                                                        <h4 className="text-sm font-interSemiBold text-[#101828]">
                                                            Preliminary Relevance {insightsView === "heatmap" ? "Heatmap" : "Ranking"}
                                                        </h4>
                                                        <p className="text-[11px] text-[#4F6A9F]">
                                                            {ranked.length} results against {patentData?.patent_number}
                                                        </p>
                                                    </div>
                                                    {/* Toggle */}
                                                    <div className="flex items-center bg-[#f0f4ff] rounded-md p-0.5">
                                                        <button
                                                            onClick={() => setInsightsView("heatmap")}
                                                            className={`px-2.5 py-1 text-[10px] font-interMedium rounded transition-colors ${
                                                                insightsView === "heatmap"
                                                                    ? "bg-[#3062D4] text-white shadow-sm"
                                                                    : "text-[#3062D4] hover:bg-[#e0e8f9]"
                                                            }`}
                                                        >
                                                            Heatmap
                                                        </button>
                                                        <button
                                                            onClick={() => { setInsightsView("list"); setRankingPage(0); }}
                                                            className={`px-2.5 py-1 text-[10px] font-interMedium rounded transition-colors ${
                                                                insightsView === "list"
                                                                    ? "bg-[#3062D4] text-white shadow-sm"
                                                                    : "text-[#3062D4] hover:bg-[#e0e8f9]"
                                                            }`}
                                                        >
                                                            List
                                                        </button>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => { setShowPreviewHeatmap(false); setRankingPage(0); }}
                                                    className="text-[#667085] hover:text-[#101828] p-1"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                            </div>



                                            {insightsView === "heatmap" ? (
                                                /* ── Heatmap View ── */
                                                <div className="border-t border-[#E6E6E6]">
                                                    {buildHeatmapSeries()[0]?.data?.length > 0 ? (
                                                        <ReactApexChart
                                                            options={heatmapOptions}
                                                            series={buildHeatmapSeries()}
                                                            type="heatmap"
                                                            height={140}
                                                        />
                                                    ) : (
                                                        <p className="text-xs text-[#4F6A9F] text-center py-6">
                                                            No relevance scores available. Run a new search to generate scores.
                                                        </p>
                                                    )}
                                                </div>
                                            ) : (
                                                /* ── List View ── */
                                                <>
                                                    {/* Column Headers */}
                                                    <div className="flex items-center px-5 py-2 text-[10px] font-interSemiBold text-[#667085] uppercase tracking-wider border-t border-[#E6E6E6]">
                                                        <span className="w-[50px]">Rank</span>
                                                        <span className="flex-1 pl-2">Result</span>
                                                        <span className="w-[200px] text-right">Relevance Score</span>
                                                    </div>

                                                    {/* Results List */}
                                                    <div>
                                                        {pageResults.length > 0 ? pageResults.map((item, idx) => {
                                                            const globalRank = rankingPage * RANKING_PAGE_SIZE + idx + 1;
                                                            const score = Math.round((item.citation.relevance_score ?? 0) * 100);
                                                            const citationId = `citation-${item.typeKey}-${item.citation.title}`;
                                                            const isSelected = selectedCitations.includes(citationId);
                                                            const barOpacity = score >= 30 ? 1 : score >= 15 ? 0.7 : 0.45;

                                                            return (
                                                                <div
                                                                    key={`rank-${globalRank}`}
                                                                    className={`relative flex items-center px-5 py-2 border-t border-[#F1F1F1] transition-colors ${isSelected ? "bg-[#f0f4ff]" : "hover:bg-[#f9fafb]"}`}
                                                                >
                                                                    {/* Rank number - click to toggle selection */}
                                                                    <div className="w-[50px] flex-shrink-0 cursor-pointer" onClick={() => handleRankingItemClick(item)}>
                                                                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-interSemiBold ${
                                                                            isSelected ? "bg-[#3062D4] text-white" : "bg-[#f0f4ff] text-[#3062D4]"
                                                                        }`}>
                                                                            {globalRank}
                                                                        </span>
                                                                    </div>
                                                                    {/* Title/link - click to open link, hover for thumbnail */}
                                                                    <div
                                                                        className="flex-1 min-w-0 pr-4 pl-2 relative"
                                                                        onMouseEnter={() => setHoveredRankItem(globalRank)}
                                                                        onMouseLeave={() => setHoveredRankItem(null)}
                                                                    >
                                                                        <div className="relative inline-block overflow-visible min-w-0">
                                                                            {item.citation.is_new && (
                                                                                <span
                                                                                    className="absolute -top-1.5 -right-1 w-2.5 h-2.5 rounded-full bg-[#E12121] inline-block z-10 cursor-pointer"
                                                                                    title="New product found by monitoring"
                                                                                />
                                                                            )}
                                                                            <p
                                                                                className="text-sm font-interMedium text-[#101828] truncate hover:text-[#3062D4] cursor-pointer"
                                                                                title={item.citation.title}
                                                                                onClick={() => {
                                                                                    if (item.citation.link) window.open(item.citation.link, '_blank');
                                                                                }}
                                                                            >
                                                                                {item.citation.title}
                                                                            </p>
                                                                        </div>
                                                                        {/* Thumbnail/favicon on hover */}
                                                                        {hoveredRankItem === globalRank && item.citation.link && (
                                                                            <div className="absolute left-0 top-[-80px] z-50 bg-white border border-[#d0daf5] rounded-lg shadow-lg p-2 flex flex-col items-center gap-1.5">
                                                                                {item.citation.thumbnail ? (
                                                                                    <img
                                                                                        src={item.citation.thumbnail}
                                                                                        alt=""
                                                                                        className="w-[140px] h-[90px] object-cover rounded"
                                                                                        onError={(e: any) => {
                                                                                            e.target.onerror = null;
                                                                                            e.target.src = `https://www.google.com/s2/favicons?domain=${new URL(item.citation.link).hostname}&sz=64`;
                                                                                            e.target.className = "w-8 h-8";
                                                                                        }}
                                                                                    />
                                                                                ) : (
                                                                                    <div className="flex items-center gap-2">
                                                                                        <img
                                                                                            src={`https://www.google.com/s2/favicons?domain=${new URL(item.citation.link).hostname}&sz=32`}
                                                                                            alt=""
                                                                                            className="w-5 h-5"
                                                                                        />
                                                                                        <span className="text-[10px] text-[#4F6A9F] max-w-[140px] truncate">
                                                                                            {new URL(item.citation.link).hostname}
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                                <p className="text-[10px] text-[#667085] max-w-[160px] truncate">{item.citation.link}</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {/* Score badge - color coded, click to toggle selection */}
                                                                    <div
                                                                        className="w-[200px] flex items-center justify-end gap-2 flex-shrink-0 cursor-pointer"
                                                                        onClick={() => handleRankingItemClick(item)}
                                                                    >
                                                                        <div className="flex-1 h-2 bg-[#E8EDF5] rounded-full overflow-hidden">
                                                                            <div
                                                                                className="h-full rounded-full"
                                                                                style={{
                                                                                    width: `${score}%`,
                                                                                    backgroundColor: score >= 70 ? "#16a34a" : score >= 40 ? "#ca8a04" : "#dc2626",
                                                                                }}
                                                                            />
                                                                        </div>
                                                                        <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-interMedium rounded-full ${
                                                                            score >= 70
                                                                                ? "bg-green-100 text-green-800"
                                                                                : score >= 40
                                                                                    ? "bg-yellow-100 text-yellow-800"
                                                                                    : "bg-red-100 text-red-800"
                                                                        }`}>
                                                                            {score}%
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }) : (
                                                            <p className="text-xs text-[#4F6A9F] text-center py-6">
                                                                No relevance scores available. Run a new search to generate scores.
                                                            </p>
                                                        )}
                                                    </div>
                                                </>
                                            )}

                                            {/* Footer */}
                                            <div className="flex items-center justify-between px-5 py-2 border-t border-[#E6E6E6] bg-[#f9fafb]">
                                                <div className="flex items-center gap-1.5 text-[10px] text-[#667085]">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-[#3062D4]" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                    </svg>
                                                    Preliminary analysis only. Vijilize for advanced claim-level scoring.
                                                </div>
                                                {insightsView === "list" && totalPages > 1 && (
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            onClick={() => setRankingPage(Math.max(0, rankingPage - 1))}
                                                            disabled={rankingPage === 0}
                                                            className={`px-2 py-1 text-xs font-interMedium rounded ${rankingPage === 0 ? "text-[#D0D5DD] cursor-not-allowed" : "text-[#3062D4] hover:bg-[#f0f4ff]"}`}
                                                        >
                                                            Prev
                                                        </button>
                                                        {Array.from({ length: totalPages }, (_, i) => (
                                                            <button
                                                                key={i}
                                                                onClick={() => setRankingPage(i)}
                                                                className={`w-6 h-6 text-xs font-interMedium rounded ${rankingPage === i ? "bg-[#3062D4] text-white" : "text-[#344054] hover:bg-[#f0f4ff]"}`}
                                                            >
                                                                {i + 1}
                                                            </button>
                                                        ))}
                                                        <button
                                                            onClick={() => setRankingPage(Math.min(totalPages - 1, rankingPage + 1))}
                                                            disabled={rankingPage === totalPages - 1}
                                                            className={`px-2 py-1 text-xs font-interMedium rounded ${rankingPage === totalPages - 1 ? "text-[#D0D5DD] cursor-not-allowed" : "text-[#3062D4] hover:bg-[#f0f4ff]"}`}
                                                        >
                                                            Next
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className={`flex px-6 ${showPreviewHeatmap ? "h-[calc(100vh-560px)] min-h-[250px]" : "h-[calc(100vh-260px)]"} overflow-hidden`}>
                                {/* Left Sidebar - Citation Lists */}
                                <div className="min-w-[35%] bg-[#F9FAFB] overflow-y-auto rounded-lg mr-6">
                                    <div className="p-4">
                                        <h3 className="text-base font-interMedium mb-4">
                                            Products List
                                        </h3>
                                        <div className="space-y-4">
                                            {citationTypes.map((type, index) => {
                                                const typeData = citationsData?.[type.key];
                                                const results = typeData?.results || [];
                                                const hasData = results.length > 0;


                                                return (
                                                    <div
                                                        key={type.key}
                                                        className={`cursor-pointer relative ${!hasData ? "opacity-50" : ""}`}
                                                        onClick={() => handleListClick(index, type.key)}
                                                    >
                                                        {type.key === activeListType && (
                                                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#3062D4]"></div>
                                                        )}
                                                        <div className="flex items-start justify-between px-2 py-2 hover:bg-white">
                                                            <div className="flex items-start gap-x-3">
                                                                <img
                                                                    src={allImgPaths.folderIcon}
                                                                    alt="Folder"
                                                                    className="w-5 h-5 mt-0.5"
                                                                />
                                                                <div>
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="text-sm font-interMedium text-[#101828]">
                                                                            {type.title}
                                                                        </div>
                                                                        {hasCitationTypeNewItems(type.key) && (
                                                                            <span
                                                                                className="w-2.5 h-2.5 rounded-full bg-[#E12121] inline-block"
                                                                                title="New products found by monitoring"
                                                                            />
                                                                        )}
                                                                        {!hasData && (
                                                                            <span className="px-2 py-0.5 text-xs font-interMedium bg-gray-100 text-gray-500 rounded-full">
                                                                                No results
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-x-2 whitespace-nowrap">
                                                                <span className="text-xs text-[#7F7D83] whitespace-nowrap">
                                                                    {`${getSelectedCountForListType(
                                                                        type.key
                                                                    )} of ${results.length}`}
                                                                </span>
                                                                <div className="text-gray-400 flex-shrink-0">
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        className="h-5 w-5"
                                                                        viewBox="0 0 20 20"
                                                                        fill="currentColor"
                                                                    >
                                                                        <path
                                                                            fillRule="evenodd"
                                                                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                                            clipRule="evenodd"
                                                                        />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                                {/* Main Content - Patent Lists */}
                                <div className="flex-1 overflow-y-auto">
                                    <div>
                                        {/* Search Bar and USPTO Link */}
                                        <div className="mb-4 flex items-center justify-start gap-x-4">
                                            {documentTransactionHistory ? (
                                                <>
                                                    <a
                                                        href={documentTransactionHistory}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center text-blue-600 hover:text-blue-800 hover:underline font-interMedium text-sm"
                                                    >
                                                        USPTO Patent Center
                                                        <img
                                                            src={allImgPaths.externalLinkIcon}
                                                            alt="External Link"
                                                            className="ml-1 w-3 h-3 text-blue-600"
                                                        />
                                                    </a>
                                                    <div className="w-[70%]">
                                                        <SearchBar
                                                            img_url={allImgPaths.searchIcon}
                                                            placeholder="Search"
                                                            width="w-full"
                                                            value={searchQuery}
                                                            onChange={(value: string) =>
                                                                setSearchQuery(value)
                                                            }
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <SearchBar
                                                        img_url={allImgPaths.searchIcon}
                                                        placeholder="Search"
                                                        width="w-full"
                                                        value={searchQuery}
                                                        onChange={(value: string) => setSearchQuery(value)}
                                                    />
                                                    <div className="w-[20%]">
                                                        <CustomSelect
                                                            options={[
                                                                { label: '5', value: '5' },
                                                                { label: '10', value: '10' },
                                                                { label: '15', value: '15' },
                                                                { label: '20', value: '20' }
                                                            ]}
                                                            onChange={(e: any) => { handleAPIChange(+e.value) }}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        {/* Calculate filtered citations once and reuse */}
                                        {(function () {
                                            const providerDisplayNames: Record<string, string> = {
                                                'serper': 'Serper',
                                                'search_api': 'Search API',
                                                'serp_api': 'SERP API',
                                                'agentic_search': 'OpenAI Search',
                                                'gemini_agentic_search': 'Gemini Search',
                                                'search_api_image': 'Search API (Image)',
                                                'serp_api_image': 'SERP API (Image)'
                                            };
                                            const providerColors: Record<string, { active: string; inactive: string }> = {
                                                'serper': { active: 'bg-blue-600 text-white', inactive: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
                                                'search_api': { active: 'bg-purple-600 text-white', inactive: 'bg-purple-50 text-purple-700 hover:bg-purple-100' },
                                                'serp_api': { active: 'bg-amber-600 text-white', inactive: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
                                                'agentic_search': { active: 'bg-emerald-600 text-white', inactive: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
                                                'gemini_agentic_search': { active: 'bg-cyan-600 text-white', inactive: 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100' },
                                                'search_api_image': { active: 'bg-pink-600 text-white', inactive: 'bg-pink-50 text-pink-700 hover:bg-pink-100' },
                                                'serp_api_image': { active: 'bg-orange-600 text-white', inactive: 'bg-orange-50 text-orange-700 hover:bg-orange-100' }
                                            };

                                            const allResults = citationsData?.[activeListType]?.results || [];

                                            // Get available providers for this category
                                            const availableProviders: string[] = [];
                                            const providerCounts: Record<string, number> = {};
                                            allResults.forEach((r: any) => {
                                                const p = r.provider || 'unknown';
                                                providerCounts[p] = (providerCounts[p] || 0) + 1;
                                                if (!availableProviders.includes(p)) availableProviders.push(p);
                                            });

                                            // Filter by active provider, then by search query
                                            const filteredCitations = allResults
                                                .filter((citation: any) => citation.provider === activeProvider)
                                                .filter((citation: any) => {
                                                    if (searchQuery.trim() === "") return true;
                                                    const normalizedCitation = citation?.title
                                                        ?.toLowerCase()
                                                        ?.replace(/[,\/]/g, "");
                                                    const normalizedQuery = searchQuery
                                                        .toLowerCase()
                                                        .replace(/[,\/]/g, "");
                                                    return (
                                                        normalizedCitation.includes(normalizedQuery) ||
                                                        citation?.title
                                                            ?.toLowerCase()
                                                            ?.includes(searchQuery.toLowerCase())
                                                    );
                                                })
                                                .sort((a: any, b: any) => (b.relevance_score ?? 0) - (a.relevance_score ?? 0));

                                            // If loading, show spinner
                                            if (isLoading) {
                                                return (
                                                    <div className="flex justify-center items-center h-40">
                                                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#1751D0]"></div>
                                                    </div>
                                                );
                                            }

                                            // If error, show error message
                                            if (error) {
                                                return (
                                                    <div className="text-center py-8">
                                                        <p className="text-red-500">{error}</p>
                                                        <button
                                                            onClick={fetchExaminerCitations}
                                                            className="mt-4 px-4 py-2 bg-[#1751D0] text-white rounded-md hover:bg-[#113B98]"
                                                        >
                                                            Retry
                                                        </button>
                                                    </div>
                                                );
                                            }

                                            // If no citations at all for this tab
                                            if (allResults.length === 0) {
                                                return (
                                                    <div className="flex flex-col items-center justify-center h-[calc(100vh-520px)] text-center">
                                                        <p className="text-gray-500 font-interMedium">
                                                            No results yet for{" "}
                                                            {citationTypes.find((t) => t.key === activeListType)?.title}.
                                                        </p>
                                                        <p className="text-sm text-gray-400 mt-1">
                                                            Results will appear here once the backend returns data for this section.
                                                        </p>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <>
                                                    {/* Provider Tabs */}
                                                    <div className="flex items-center gap-2 mb-4">
                                                        {availableProviders.map((provider) => {
                                                            const isActive = activeProvider === provider;
                                                            const colors = providerColors[provider] || { active: 'bg-gray-600 text-white', inactive: 'bg-gray-50 text-gray-700 hover:bg-gray-100' };
                                                            return (
                                                                <button
                                                                    key={provider}
                                                                    onClick={() => setActiveProvider(provider)}
                                                                    className={`px-3 py-1.5 text-xs font-interSemiBold rounded-full transition-colors ${isActive ? colors.active : colors.inactive}`}
                                                                >
                                                                    {providerDisplayNames[provider] || provider}
                                                                    <span className={`ml-1.5 ${isActive ? 'opacity-80' : 'opacity-60'}`}>
                                                                        ({providerCounts[provider] || 0})
                                                                    </span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>

                                                    {/* References count */}
                                                    <div className="mb-4">
                                                        <h3 className="text-base font-interSemiBold text-[#272E35]">
                                                            {filteredCitations.length > 1
                                                                ? `${filteredCitations.length} References Found`
                                                                : `${filteredCitations.length} Reference Found`}
                                                        </h3>
                                                        <p className="text-sm text-[#272E35] font-interRegular">
                                                            Selected patents will be saved in the project's
                                                            Patents tab for future vijilizations.
                                                        </p>
                                                    </div>

                                                    {/* No results for search within this provider */}
                                                    {filteredCitations.length === 0 && searchQuery.trim() !== "" ? (
                                                        <div className="flex items-center justify-center h-[calc(100vh-580px)]">
                                                            <p className="text-gray-500 text-lg font-interMedium">
                                                                No results found for "{searchQuery}"
                                                            </p>
                                                        </div>
                                                    ) : filteredCitations.length === 0 ? (
                                                        <div className="flex items-center justify-center h-[calc(100vh-580px)]">
                                                            <p className="text-gray-500 font-interMedium">
                                                                No results from {providerDisplayNames[activeProvider] || activeProvider} for this category.
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        /* Results List */
                                                        <div className="space-y-3 overflow-y-auto overflow-x-hidden h-[calc(100vh-580px)]">
                                                            {filteredCitations.map((citation: any, index: number) => {
                                                                const citationId = `citation-${activeListType}-${citation?.title}`;

                                                                return (
                                                                    <div
                                                                        key={citationId + index}
                                                                        className={`flex items-start gap-x-3 p-3 rounded-lg border-b border-[#F1F1F1]`}
                                                                    >
                                                                        <div className="flex-shrink-0">
                                                                            <CustomCheckBox
                                                                                id={citationId}
                                                                                name={citationId}
                                                                                label=""
                                                                                checked={selectedCitations.includes(
                                                                                    citationId
                                                                                )}
                                                                                onChange={(
                                                                                    name: string,
                                                                                    checked: boolean
                                                                                ) => {
                                                                                    handleCitationSelect(
                                                                                        citationId,
                                                                                        checked
                                                                                    );
                                                                                }}
                                                                                customStyle={`flex-shrink-0 `}
                                                                                disabled={false}
                                                                            />
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 cursor-pointer"
                                                                            onClick={() => {
                                                                                handleCitationSelect(
                                                                                    citationId,
                                                                                    !selectedCitations.includes(
                                                                                        citationId
                                                                                    )
                                                                                );
                                                                            }}
                                                                        >
                                                                            <div className="w-[555px]">
                                                                                <div className="flex items-center gap-2">
                                                                                    {citation?.thumbnail && (
                                                                                        <img
                                                                                            src={citation.thumbnail}
                                                                                            alt=""
                                                                                            className="w-10 h-10 object-cover rounded flex-shrink-0"
                                                                                            onError={(e: any) => { e.target.style.display = 'none'; }}
                                                                                        />
                                                                                    )}
                                                                                    <div className="relative inline-block overflow-visible">
                                                                                        {citation?.is_new && (
                                                                                            <span
                                                                                                className="absolute -top-1.5 -right-1 w-2.5 h-2.5 rounded-full bg-[#E12121] inline-block z-10 cursor-pointer"
                                                                                                title="New product found by monitoring"
                                                                                            />
                                                                                        )}
                                                                                        <h4
                                                                                            title={citation?.title}
                                                                                            className={`text-sm font-interSemiBold hover:text-[#3062D4] cursor-pointer`}
                                                                                            onClick={(e: any) => {
                                                                                                e.stopPropagation();
                                                                                                if (citation?.link) {
                                                                                                    window.open(citation.link, '_blank');
                                                                                                }
                                                                                            }}
                                                                                        >
                                                                                            {citation?.title}
                                                                                        </h4>
                                                                                    </div>
                                                                                    {citation?.relevance_score != null && (
                                                                                        <span
                                                                                            className={`flex-shrink-0 px-2 py-0.5 text-xs font-interMedium rounded-full ${
                                                                                                citation.relevance_score >= 0.7
                                                                                                    ? "bg-green-100 text-green-800"
                                                                                                    : citation.relevance_score >= 0.4
                                                                                                        ? "bg-yellow-100 text-yellow-800"
                                                                                                        : "bg-red-100 text-red-800"
                                                                                            }`}
                                                                                            title="Relevance score from Cohere reranker"
                                                                                        >
                                                                                            {(citation.relevance_score * 100).toFixed(0)}%
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Vijilize Step Content */}
                            <div className="px-6 h-[calc(100vh-260px)] overflow-y-auto">
                                <div className="">
                                    {/* Notification */}
                                    <div className="border border-[#ECECED] rounded-lg px-3 py-3 flex items-start gap-x-2 mb-3">
                                        <img src={allImgPaths.InfoIconBlue} alt="" />
                                        <p className="text-sm font-interMedium text-[#0A090B] leading-[1.6]">
                                            Each vijilization takes some time to complete. You can
                                            find your past and active ones in the Vijilizations tab.
                                        </p>
                                    </div>

                                    {/* Content */}
                                    <div className="flex gap-x-8">
                                        <div className="px-3 py-3 rounded-lg border border-[#E6E6E6] w-[434px]">
                                            <h4 className="text-sm font-interMedium text-[#0A090B] pb-3">
                                                Data Used
                                            </h4>

                                            {/* Use only Independent Claims checkbox */}
                                            <div className="mb-3">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={useOnlyIndependentClaims}
                                                        onChange={(e) => setUseOnlyIndependentClaims(e.target.checked)}
                                                        className="w-4 h-4 text-[#1751D0] border border-gray-300 rounded focus:ring-[#1751D0] focus:ring-2"
                                                    />
                                                    <span className="text-sm font-interMedium text-[#0A090B]">
                                                        Use only Independent Claims
                                                    </span>
                                                </label>
                                            </div>

                                            {/* Accordion */}
                                            <div className="overflow-y-auto max-h-60 pr-2">
                                                {/* Source Accordion */}
                                                <div
                                                    onClick={() => toggleAccordion(1)}
                                                    className={`mb-3 last:mb-0 ${activeIndex === 1 ? "" : "accordion-hide"
                                                        }`}
                                                >
                                                    {/* Header Section */}
                                                    <div className="border border-[#F0F0F0] px-2 py-2 bg-[#FDFDFD] cursor-pointer accordion-heading flex items-center justify-between">
                                                        <div className="flex items-center gap-4 w-full justify-between">
                                                            <p className="text-[#555F6D] text-sm font-interMedium">
                                                                Source{" "}
                                                                <span className="text-[#555F6D] text-sm font-interMedium">
                                                                    ({sourcePatents.length})
                                                                </span>
                                                            </p>
                                                            <img
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleAccordion(1);
                                                                }}
                                                                className="accordion-arrow cursor-pointer hover:bg-[#f2f4f7] rounded w-5"
                                                                src={allImgPaths.dropdownArrow}
                                                                alt="Toggle Dropdown"
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Content Section */}
                                                    <div
                                                        className={`accordion-content border border-[#F0F0F0] border-t-0 ${activeIndex === 1 ? "block" : "hidden"
                                                            }`}
                                                    >
                                                        {sourcePatents.length === 0 ? (
                                                            <div className="py-3 ml-3">
                                                                <p className="text-sm text-gray-500">
                                                                    No patents in source section
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            sourcePatents.map((patentId, index) => {
                                                                const patentData = selectedPatentData.find(
                                                                    (p) => p.patent_id === patentId
                                                                );
                                                                return (
                                                                    <div
                                                                        key={index}
                                                                        className="py-3 ml-3 flex justify-between items-center pr-3"
                                                                    >
                                                                        <p
                                                                            title={patentData?.file_name || patentId}
                                                                            className="text-sm font-interSemiBold text-[#1751D0] truncate max-w-[calc(100%_-_15px)] w-fit cursor-pointer"
                                                                        >
                                                                            {patentData?.file_name || patentId}
                                                                        </p>
                                                                    </div>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Compare Accordion */}
                                                <div
                                                    onClick={() => toggleAccordion(2)}
                                                    className={`mb-3 last:mb-0 ${activeIndex === 2 ? "" : "accordion-hide"
                                                        }`}
                                                >
                                                    {/* Header Section */}
                                                    <div className="border border-[#F0F0F0] px-2 py-2 bg-[#FDFDFD] cursor-pointer accordion-heading flex items-center justify-between">
                                                        <div className="flex items-center gap-4 w-full justify-between">
                                                            <p className="text-[#555F6D] text-sm font-interMedium">
                                                                Compare{" "}
                                                                <span className="text-[#555F6D] text-sm font-interMedium">
                                                                    ({comparePatents.length})
                                                                </span>
                                                            </p>
                                                            <img
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleAccordion(2);
                                                                }}
                                                                className="accordion-arrow cursor-pointer hover:bg-[#f2f4f7] rounded w-5"
                                                                src={allImgPaths.dropdownArrow}
                                                                alt="Toggle Dropdown"
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Content Section */}
                                                    <div
                                                        className={`accordion-content border border-[#F0F0F0] border-t-0 ${activeIndex === 2 ? "block" : "hidden"
                                                            }`}
                                                    >
                                                        {comparePatents.length === 0 ? (
                                                            <div className="py-3 ml-3">
                                                                <p className="text-sm text-gray-500">
                                                                    No patents in compare section
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            comparePatents.map((patentId, index) => {
                                                                const patentData = selectedPatentData.find(
                                                                    (p) => p.patent_id === patentId
                                                                );
                                                                return (
                                                                    <div
                                                                        key={index}
                                                                        className="py-3 ml-3 flex justify-between items-center pr-3"
                                                                    >
                                                                        <p
                                                                            title={patentData?.file_name || patentId}
                                                                            className="text-sm font-interSemiBold text-[#1751D0] truncate max-w-[calc(100%_-_15px)] w-fit cursor-pointer"
                                                                        >
                                                                            {patentData?.file_name || patentId}
                                                                        </p>
                                                                    </div>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Combine main url checkbox - show only if there's compare data */}
                                            {comparePatents.length > 0 && (
                                                <div className="mt-3 pt-3 border-t border-[#E6E6E6]">
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={combineMainUrl}
                                                            onChange={(e) => setCombineMainUrl(e.target.checked)}
                                                            className="w-4 h-4 text-[#1751D0] border border-gray-300 rounded focus:ring-[#1751D0] focus:ring-2"
                                                        />
                                                        <span className="text-sm font-interMedium text-[#0A090B]">
                                                            Combine main url
                                                        </span>
                                                    </label>
                                                </div>
                                            )}
                                        </div>

                                        <div className="w-1/2 flex-shrink-0">
                                            <div className="mb-6">
                                                <span className="text-[#2D2B32] font-interMedium text-sm inline-block pb-2">
                                                    Name
                                                </span>
                                                <InputTextField
                                                    label={""}
                                                    name={"vijilizeName"}
                                                    placeholder={"Enter name"}
                                                    onChange={handleNameChange}
                                                    value={vijilizeName}
                                                />
                                            </div>

                                            <div className="">
                                                <div className="pb-2 flex items-center justify-between">
                                                    <span className="text-[#2D2B32] font-interMedium text-sm inline-block">
                                                        Description
                                                    </span>
                                                    <span className="text-xs text-[#7F7D83]">
                                                        Optional
                                                    </span>
                                                </div>
                                                <Textarea
                                                    name="vijilize-description"
                                                    placeholder="Enter description"
                                                    value={vijilizeDescription}
                                                    onChange={handleDescriptionChange}
                                                    id="vijilize-description"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Footer */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-white">
                        <div className="flex justify-between max-w-full gap-4">
                            {currentStep === "citations" ? (
                                /* Citations step buttons */
                                <>
                                    <button
                                        onClick={handleClose}
                                        className="flex-1 px-4 py-3 text-sm font-interMedium text-[#4F4D55] hover:bg-gray-50 rounded-md border border-[#E6E6E6]"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleAddToProject}
                                        disabled={selectedCount === 0 || isAddingToProject}
                                        className={`flex-1 px-4 py-3 text-sm font-interMedium text-[#4F4D55] hover:bg-gray-50 border border-[#E6E6E6] rounded-md ${selectedCount === 0 || isAddingToProject
                                            ? "button-disabled"
                                            : ""
                                            }`}
                                    >
                                        Add to Project
                                    </button>
                                    <button
                                        onClick={handleVijilize}
                                        disabled={selectedCount === 0 || isLoading}
                                        className={`flex-1 px-4 py-3 text-sm font-interMedium text-white bg-[#3062D4] hover:bg-blue-700 rounded-md ${selectedCount === 0 || isLoading ? "button-disabled" : ""
                                            }`}
                                    >
                                        Next
                                    </button>
                                </>
                            ) : (
                                /* Vijilize step buttons */
                                <>
                                    <button
                                        onClick={handleBack}
                                        className="flex-1 px-4 py-3 text-sm font-interMedium text-[#4F4D55] hover:bg-gray-50 rounded-md border border-[#E6E6E6]"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSubmitVijilize}
                                        disabled={isSubmitDisabled() || isVijilizing}
                                        className={`flex-1 px-4 py-3 text-sm font-interMedium text-white bg-[#3062D4] hover:bg-blue-700 rounded-md ${isSubmitDisabled() || isVijilizing
                                            ? "button-disabled"
                                            : ""
                                            }`}
                                    >
                                        Vijilize
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <MonitorModal
                isShow={isShowMonitor}
                setIsShow={setIsShowMonitor}
                projectId={projectId}
                level="project"
            />
        </div>
    );
};

export default SimilarProductsModal;
